<template>
  <Menu as="div" class="top-0 flex w-full">
    <MenuButton class="w-full focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue">
      <button
        :class="{ 'text-light-brand-blue': owners?.length || coOwners?.length || teamMembers?.length }"
        class="btn cancel-btn !py-[9px] ml-1 w-full text-left flex justify-start text-gray-400">
        <UsersRound
          :color="owners?.length || coOwners?.length || teamMembers?.length ? 'rgb(13 138 186)' : '#899399'"
          class="mr-2 w-5 h-5" />
        {{ $t("Search by People") }} <ChevronDown class="w-5 h-5 ml-4" aria-hidden="true" />
      </button>
    </MenuButton>
    <transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0">
      <MenuItems
        class="absolute font-normal cursor-pointer z-50 w-[540px] mx-3 mt-10 -ml-72 origin-bottom-right bg-white shadow-lg rounded-lg focus:outline-none">
        <div class="w-full">
          <div
            class="flex w-full h-full overflow-hidden transition ease-in-out rounded-t-lg duration-side-menu-transition-duration dark:bg-dark-screen-background">
            <!-- Content area -->
            <div class="flex flex-col flex-1 w-full">
              <main class="flex-1 w-full overflow-y-auto focus:outline-none">
                <div class="relative w-full">
                  <div class="flex-col w-full pt-0 pb-2">
                    <div class="w-full px-4 sm:px-6 md:px-0">
                      <div class="w-full py-">
                        <!-- Tabs -->
                        <div class="w-full">
                          <div
                            class="border-b-[1px] pt-2 bg-light-screen-background border-light-divider-border dark:border-dark-divider-border">
                            <nav class="flex -mb-1 space-x-4">
                              <div
                                v-for="tab in tabs"
                                :key="tab.value"
                                :class="[
                                  tab.value == current_tab
                                    ? 'text-light-brand-blue dark:text-dark-brand-blue'
                                    : 'text-light-body-text dark:text-common-grey hover:border-light-divider-border dark:hover:border-dark-divider-border hover:text-black dark:hover:text-gray-300',
                                  'flex justify-start items-start whitespace-nowrap py-3 mb-1 px-0 border-b-[2px] relative transition ease-in-out duration-side-menu-transition-duration bg-black/0 text-sm font-normal select-none border-transparent'
                                ]"
                                @click="current_tab = tab.value">
                                <!-- Active bottom div for highlighting -->
                                <div
                                  :class="[
                                    current_tab == tab.value ? '' : 'scale-0',
                                    'absolute pointer-events-none transition transform ease-in-out duration-side-menu-transition-duration w-full h-[2px] rounded-full  bg-light-brand-blue dark:bg-dark-brand-blue -bottom-0.5'
                                  ]" />
                                <component
                                  :is="tab.icon"
                                  :color="current_tab == tab.value ? 'rgb(13 138 186)' : '#899399'"
                                  :class="[current_tab == tab.value ? 'text-blue' : 'text-gray-400 group-hover:text-gray-500', 'mx-2']"
                                  :active="current_tab == tab.value"
                                  :dark-mode="$store.state.ui.darkMode" />
                                <span class="truncate">
                                  {{ tab.name }}
                                </span>
                              </div>
                            </nav>
                          </div>
                        </div>
                        <OwnersFilterTab v-if="current_tab == 'owners'" v-model:selected-owners="selected_owners" :teams="teams" />
                        <CoOwnersFilterTab
                          v-if="current_tab == 'co_owners'"
                          v-model:selected-co-owners="selected_co_owners"
                          :teams="teams" />
                        <TeamMembersFilterTab
                          v-if="current_tab == 'team_members'"
                          v-model:selected-team-members="selected_team_members"
                          :teams="teams" />
                      </div>
                    </div>
                  </div>
                </div>
              </main>
            </div>
          </div>
        </div>
      </MenuItems>
    </transition>
  </Menu>
</template>

<script>
import { Menu, MenuButton, MenuItems } from "@headlessui/vue"
import OwnersFilterTab from "./OwnersFilterTab.vue"
import CoOwnersFilterTab from "./CoOwnersFilterTab.vue"
import TeamMembersFilterTab from "./TeamMembersFilterTab.vue"
import { ChevronDown, Handshake, UsersRound, ShieldEllipsis } from "lucide-vue-next"

export default {
  components: {
    Menu,
    MenuButton,
    MenuItems,
    OwnersFilterTab,
    CoOwnersFilterTab,
    TeamMembersFilterTab,
    UsersRound,
    ChevronDown,
    ShieldEllipsis,
    Handshake
  },
  props: {
    owners: {
      type: Array,
      default: () => []
    },
    coOwners: {
      type: Array,
      default: () => []
    },
    teamMembers: {
      type: Array,
      default: () => []
    }
  },
  emits: ["update:owners", "update:coOwners", "update:teamMembers"],
  data() {
    return {
      teams: this.$store.state.data.teams,
      current_tab: "owners",
      tabs: [
        { name: this.$t("Owners"), value: "owners", icon: "ShieldEllipsis" },
        { name: this.$t("Co-Owners"), value: "co_owners", icon: "Handshake" },
        { name: this.$t("Team Members"), value: "team_members", icon: "UsersRound" }
      ],
      selected_owners: [],
      selected_co_owners: [],
      selected_team_members: [],
      avoid_watch_trigger: false
    }
  },
  watch: {
    selected_owners() {
      if (!this.avoid_watch_trigger) {
        this.$emit("update:owners", this.selected_owners)
      }
    },
    selected_co_owners() {
      if (!this.avoid_watch_trigger) {
        this.$emit("update:coOwners", this.selected_co_owners)
      }
    },
    selected_team_members() {
      if (!this.avoid_watch_trigger) {
        this.$emit("update:teamMembers", this.selected_team_members)
      }
    }
  },

  created() {
    this.init()
  },

  methods: {
    init() {
      this.avoid_watch_trigger = true
      this.selected_owners = this.owners
      this.selected_co_owners = this.coOwners
      this.selected_team_members = this.teamMembers
      this.avoid_watch_trigger = false
    }
  }
}
</script>
